/**
 * The assistant: a message list, an image/camera button, and inline proposal cards.
 *
 * The transcript lives on the server now, keyed by a conversation id this component
 * carries. It used to live here and be posted back with every request, which cost three
 * things that all showed up as "it forgot": an image lasted exactly one turn, tool
 * results were flattened to the names of the tools, and reloading the page threw the
 * conversation away. Now the page loads whatever thread is in progress, receipts and all.
 *
 * Below the composer, "What I did here" shows the durable `ai_actions` record to everyone
 * in the group, because trust in this thing comes from being able to audit it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Modal } from "../../components/ui/modal";
import { Spinner } from "../../components/ui/spinner";
import { useBalances, useGroup, useGroupId } from "../../components/layout/AppShell";
import { ApiError, api, type Transaction } from "../../lib/api";
import { formatMoney, formatSigned, toneClass } from "../../lib/money";
import { downscaleAll } from "../../lib/images";
import { cn, newId } from "../../lib/utils";
import { useMembers } from "../ledger/useLedger";
import { CameraIcon, PhotoIcon } from "../../components/ui/icons";
import { RichText } from "./RichText";

interface DraftLine {
  name: string;
  amount_original_cents: number;
  assignee_member_ids: number[];
}

interface DraftParticipant {
  member_id: number;
  individual_original_cents?: number;
  percentage_bp?: number | null;
  shares_group_cost?: boolean;
}

interface ProposalDraft {
  type?: string;
  name?: string;
  occurred_on?: string;
  total_original_cents?: number;
  currency_code?: string | null;
  payer_member_id?: number | null;
  from_member_id?: number | null;
  to_member_id?: number | null;
  split?: string | null;
  participants?: DraftParticipant[];
  line_items?: DraftLine[];
  group_cost_original_cents?: number;
  individual_source?: string;
  [key: string]: unknown;
}

interface PreviewShare {
  member_id: number;
  share_original_cents: number;
  share_base_cents: number;
}

interface DuplicateCandidate {
  transaction_id: number;
  name: string;
  occurred_on: string;
  total_base_cents: number;
}

interface ProposalPreview {
  total_base_cents: number;
  total_original_cents?: number;
  ledger: Record<string, number>;
  shares?: PreviewShare[];
  derived_tax_tip_original_cents?: number;
  warnings?: string[];
  possible_duplicates?: DuplicateCandidate[];
}

interface Proposal {
  kind: "expense" | "payment" | "edit" | "delete";
  draft?: ProposalDraft;
  preview?: ProposalPreview;
  transaction_id?: number;
  editable?: boolean;
  /** Why this cannot be changed, when it cannot: "it is locked", "not yours". */
  refused_because?: string | null;
  reason?: string | null;
  summary?: { name: string; occurred_on: string; total_base_cents: number };
}

interface Message {
  id: string;
  role: "user" | "model";
  text: string;
  /**
   * What to show for the images on this message. A message just sent carries object URLs
   * for the local files; one loaded back from the server carries `/api/…/ai/images/<id>`.
   * Both are just `src` values, so the transcript does not care which it has.
   */
  images?: string[];
  /** Whether `images` holds object URLs this component owns and must revoke. */
  localImages?: boolean;
  tools?: string[];
  proposal?: { aiActionId: number; proposal: Proposal };
  resolved?: "confirmed" | "rejected";
}

interface ConversationMessage {
  id: number;
  role: "user" | "model";
  text: string;
  tools: string[];
  images: { id: number; url: string }[];
  ai_action_id: number | null;
  proposal: (Proposal & { resolved?: string }) | null;
  resolved: string | null;
}

interface Conversation {
  id: number;
  messages: ConversationMessage[];
}

/** Matches `ai.max_images_per_message` in the config. */
const MAX_IMAGES = 5;

/** The date where the person is, which is not always the date where the server is. */
function localToday(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

/** A stored message, in the shape the transcript renders. */
function fromServer(message: ConversationMessage): Message {
  const resolved =
    message.resolved === "confirmed" || message.resolved === "rejected"
      ? message.resolved
      : undefined;
  return {
    id: `srv-${message.id}`,
    role: message.role,
    text: message.text,
    images: message.images.map((image) => image.url),
    localImages: false,
    tools: message.tools,
    proposal:
      message.proposal && message.ai_action_id !== null
        ? { aiActionId: message.ai_action_id, proposal: message.proposal }
        : undefined,
    resolved,
  };
}

interface AIActionRow {
  id: number;
  tool: string;
  kind: string;
  status: string;
  created_at: string;
  latency_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  transaction_id: number | null;
}

export function AssistantPage() {
  const groupId = useGroupId();
  const group = useGroup(groupId);
  const members = useMembers(groupId);
  const balances = useBalances(groupId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const currency = group.data?.base_currency ?? "USD";
  const names = new Map((members.data ?? []).map((member) => [member.id, member.display_name]));

  /**
   * The thread on the server. Sent with each message so the assistant knows which
   * conversation this is; a ref rather than state because `sendTurn` reads it mid-flight
   * and a re-render in between would hand it a stale value.
   */
  const conversationId = useRef<number | null>(null);
  const [loadingThread, setLoadingThread] = useState(true);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Pick up whatever conversation is already in progress here. Reloading the page, or
  // coming back from the ledger after confirming an entry, used to wipe the chat — and
  // with it the receipt the next message was about.
  useEffect(() => {
    let cancelled = false;
    setLoadingThread(true);
    conversationId.current = null;
    setMessages([]);
    api
      .get<Conversation | null>(`/groups/${groupId}/ai/conversation`)
      .then((thread) => {
        if (cancelled || !thread) return;
        conversationId.current = thread.id;
        setMessages(thread.messages.map(fromServer));
      })
      .catch(() => {
        // A thread we cannot load is not worth an error banner: the composer works, and
        // the next message simply starts a new one.
      })
      .finally(() => {
        if (!cancelled) setLoadingThread(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  // Object URLs for the attachment thumbnails, revoked together when the set changes —
  // a pasted screenshot is a blob in memory and stays one until it is let go.
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const [dragging, setDragging] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  /**
   * Object URLs belonging to *sent* messages. The composer's previews are revoked the
   * moment `files` changes, which is right for a thumbnail you are still editing and
   * wrong for one already in the transcript — so sent images get their own, released
   * together when the chat is cleared or the page goes away.
   */
  const sentUrls = useRef<string[]>([]);
  useEffect(
    () => () => {
      sentUrls.current.forEach((url) => URL.revokeObjectURL(url));
      sentUrls.current = [];
    },
    [],
  );

  /** "New chat": end the thread on the server too, so its images are let go. */
  const clearChat = () => {
    const ending = conversationId.current;
    sentUrls.current.forEach((url) => URL.revokeObjectURL(url));
    sentUrls.current = [];
    conversationId.current = null;
    setMessages([]);
    setError(null);
    if (ending !== null) {
      void api
        .delete(`/groups/${groupId}/ai/conversation/${ending}`)
        .catch(() => {
          // Already gone, or offline. The thread is closed here either way, and the
          // server retires stale ones on its own.
        })
        .finally(() => void actions.refetch());
    }
  };

  /** Take images from the file picker, a paste, or a drop — shrunk before they go up. */
  const acceptImages = async (incoming: File[]) => {
    const images = incoming.filter((file) => file.type.startsWith("image/"));
    if (images.length === 0) return;
    const shrunk = await downscaleAll(images);
    setFiles((current) => [...current, ...shrunk].slice(0, MAX_IMAGES));
  };

  const actions = useQuery({
    queryKey: ["ai-actions", groupId],
    queryFn: () => api.get<AIActionRow[]>(`/groups/${groupId}/ai/actions?limit=25`),
  });

  const confirm = useMutation({
    mutationFn: (input: { actionId: number; draft: ProposalDraft; kind: Proposal["kind"] }) =>
      api.post<Transaction>(`/groups/${groupId}/ai/actions/${input.actionId}/confirm`, {
        draft: input.draft,
      }),
    onSuccess: (transaction, variables) => {
      setMessages((current) =>
        current.map((message) =>
          message.proposal?.aiActionId === variables.actionId
            ? { ...message, resolved: "confirmed" }
            : message,
        ),
      );
      void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
      void balances.refetch();
      void actions.refetch();

      // Land on the entry itself, in the ledger, with its detail open. Confirming used
      // to leave you on the chat looking at a card that said "Added to the ledger" —
      // true, but you had to go and find it to believe it. The conversation is on the
      // server now, so leaving the page costs nothing: the assistant tab is exactly as
      // you left it. A deletion has no entry to open, so it stays put.
      if (variables.kind !== "delete") {
        navigate(`/g/${groupId}?entry=${transaction.id}`);
      }
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : "Could not save."),
  });

  const reject = useMutation({
    mutationFn: (actionId: number) =>
      api.post(`/groups/${groupId}/ai/actions/${actionId}/reject`),
    onSuccess: (_result, actionId) => {
      setMessages((current) =>
        current.map((message) =>
          message.proposal?.aiActionId === actionId
            ? { ...message, resolved: "rejected" }
            : message,
        ),
      );
      void actions.refetch();
    },
  });

  async function send() {
    // An outer net. Everything below *should* be inside the inner try, but a throw on the
    // way there used to leave the button looking dead — no request, no error, nothing.
    // That is exactly how a missing `crypto.randomUUID` on a plain-HTTP phone presented.
    try {
      await sendTurn();
    } catch (cause) {
      console.error("assistant send failed", cause);
      setError(cause instanceof Error ? cause.message : "Could not send that message.");
      setBusy(false);
    }
  }

  async function sendTurn() {
    if ((!input.trim() && files.length === 0) || busy) return;
    const text = input.trim();
    const kept = files.map((file) => URL.createObjectURL(file));
    sentUrls.current.push(...kept);
    const outgoing: Message = {
      id: newId("msg"),
      role: "user",
      text,
      images: kept,
      localImages: true,
    };
    setMessages((current) => [...current, outgoing]);
    setInput("");
    setBusy(true);
    setError(null);

    const form = new FormData();
    form.set("message", text);
    // The browser's calendar date. The server runs in UTC, so after about 4pm on the US
    // west coast its idea of "today" is already tomorrow, and an entry dated by the
    // assistant landed on the wrong day.
    form.set("client_date", localToday());
    // The thread, not the transcript. Everything said so far — including the receipt from
    // three messages ago — is already on the server; sending an id instead of a
    // reconstruction is what makes the assistant able to look at that picture again.
    if (conversationId.current !== null) {
      form.set("conversation_id", String(conversationId.current));
    }
    const sent = files;
    for (const file of sent) form.append("images", file);
    setFiles([]);

    const reply: Message = { id: newId("msg"), role: "model", text: "", tools: [] };
    setMessages((current) => [...current, reply]);

    try {
      const response = await fetch(`/api/groups/${groupId}/ai/chat`, {
        method: "POST",
        body: form,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.cookie.match(/(?:^|;\s*)sw_csrf=([^;]*)/)?.[1] ?? "",
        },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error?.message ?? "The assistant is unavailable.");
      }

      const apply = (chunk: string) => {
        const eventLine = chunk.split("\n").find((line) => line.startsWith("event: "));
        const dataLine = chunk.split("\n").find((line) => line.startsWith("data: "));
        if (!eventLine || !dataLine) return;
        const type = eventLine.slice(7).trim();
        const data = JSON.parse(dataLine.slice(6)) as Record<string, unknown>;

        // Always the first event of a turn: which thread this message landed in. A new
        // conversation only gets its id here, and the next message needs it.
        if (type === "conversation") {
          conversationId.current = Number(data.conversation_id);
          return;
        }

        setMessages((current) =>
          current.map((message) => {
            if (message.id !== reply.id) return message;
            if (type === "token") {
              return { ...message, text: message.text + String(data.text ?? "") };
            }
            if (type === "tool_call") {
              return { ...message, tools: [...(message.tools ?? []), String(data.name)] };
            }
            if (type === "proposal") {
              return {
                ...message,
                proposal: {
                  aiActionId: Number(data.ai_action_id),
                  proposal: data.proposal as Proposal,
                },
              };
            }
            if (type === "error") {
              return { ...message, text: String(data.message ?? "Something went wrong.") };
            }
            return message;
          }),
        );
      };

      if (response.body) {
        // Parse the SSE stream by hand: one small reader beats a dependency.
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() ?? "";
          for (const chunk of chunks) apply(chunk);
        }
        if (buffer.trim()) apply(buffer);
      } else {
        // No readable stream. Older iOS Safari does not expose `response.body`, and a
        // service worker that buffers a response takes it away too — in both cases the
        // whole reply is already here, so read it in one go rather than failing. This is
        // what made the assistant look broken on a phone while working on a desktop.
        for (const chunk of (await response.text()).split("\n\n")) apply(chunk);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The assistant is unavailable.");
      // Give the message back rather than swallowing it: a failed turn that also loses
      // what you typed reads as "the app cannot send messages".
      setMessages((current) =>
        current.filter((entry) => entry.id !== reply.id && entry.id !== outgoing.id),
      );
      setInput(text);
      setFiles(sent);
    } finally {
      setBusy(false);
      void actions.refetch();
    }
  }

  return (
    <div
      className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden"
      // Drop anywhere on the page, not onto a small target: someone dragging a receipt
      // off their desktop should not have to aim.
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDragging(false);
      }}
      onDrop={(event) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragging(false);
        void acceptImages(Array.from(event.dataTransfer.files));
      }}
    >
      {dragging ? (
        <div className="pointer-events-none absolute inset-2 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-accent bg-accent/[0.08]">
          <p className="text-sm font-medium text-accent">Drop a receipt or screenshot</p>
        </div>
      ) : null}
      <Modal
        open={viewing !== null}
        onOpenChange={(open) => !open && setViewing(null)}
        title="Attached image"
        className="w-[min(52rem,calc(100vw-2rem))]"
      >
        {viewing ? (
          <img src={viewing} alt="The image sent to the assistant" className="w-full rounded-lg" />
        ) : null}
      </Modal>

      <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between py-3">
          <h1 className="display text-sm text-muted">Assistant</h1>
          {messages.length ? (
            <button
              type="button"
              onClick={clearChat}
              className="rounded-lg px-2.5 py-1.5 text-xs text-muted transition-colors hover:bg-ink/[0.04] hover:text-ink"
            >
              New chat
            </button>
          ) : null}
        </div>

        <div className="scroll-region min-h-0 flex-1 overflow-y-auto">
          {loadingThread && messages.length === 0 ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : null}

          {!loadingThread && messages.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted">
              <p className="mb-2">
                Tell me what you paid for, or send a receipt — take a photo, pick one from
                your camera roll, paste a screenshot, or drag an image in.
              </p>
              <p className="mb-2 text-xs">
                It remembers the whole conversation, receipt included, so you can keep
                talking: "make it 62", "Bo had the pasta", "add a 20% tip".
              </p>
              <p className="text-xs">
                Anything it suggests comes back as a proposal for you to confirm. It can
                never change the ledger on its own, and it only has member-level
                permissions.
              </p>
            </div>
          ) : null}

          <ul className="flex flex-col gap-4">
            {messages.map((message) => (
              <li
                key={message.id}
                className={cn("flex flex-col gap-2", message.role === "user" && "items-end")}
              >
                {message.images?.length ? (
                  <div className={cn("flex flex-wrap gap-1.5", message.role === "user" && "justify-end")}>
                    {message.images.map((url, index) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setViewing(url)}
                        // What you sent should stay visible: without this the receipt
                        // vanishes the moment it is uploaded and there is no way to check
                        // the assistant read the right picture.
                        aria-label={`View attached image ${index + 1}`}
                        className="overflow-hidden rounded-xl border border-rule transition-transform hover:scale-[1.02]"
                      >
                        <img src={url} alt="" className="h-24 w-24 object-cover" />
                      </button>
                    ))}
                  </div>
                ) : null}

                {message.text ? (
                  <div
                    className={cn(
                      "max-w-[85%] animate-fade-in px-3.5 py-2.5 text-sm",
                      message.role === "user"
                        ? "whitespace-pre-wrap rounded-2xl rounded-br-md bg-accent text-accent-ink"
                        : "rounded-2xl rounded-bl-md border border-rule bg-raised",
                    )}
                  >
                    {message.role === "user" ? message.text : <RichText text={message.text} />}
                  </div>
                ) : null}

                {message.tools?.length ? (
                  <p className="text-2xs text-muted">
                    looked up: {message.tools.join(", ").replace(/_/g, " ")}
                  </p>
                ) : null}

                {message.proposal ? (
                  <ProposalCard
                    card={message.proposal.proposal}
                    groupId={groupId}
                    currency={currency}
                    names={names}
                    resolved={message.resolved}
                    busy={confirm.isPending}
                    onConfirm={(draft) =>
                      confirm.mutate({
                        actionId: message.proposal!.aiActionId,
                        draft,
                        kind: message.proposal!.proposal.kind,
                      })
                    }
                    onReject={() => reject.mutate(message.proposal!.aiActionId)}
                  />
                ) : null}
              </li>
            ))}
          </ul>
          {busy ? (
            <p className="mt-3 flex items-center gap-2 text-xs text-muted">
              <Spinner /> thinking…
            </p>
          ) : null}
          <div ref={endRef} />
        </div>

        {error ? (
          <p
            role="alert"
            className="mt-2 shrink-0 rounded-lg border border-owing/35 bg-owing/[0.08] px-3 py-2 text-sm"
          >
            {error}
          </p>
        ) : null}

        {files.length ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {files.map((file, index) => (
              <span key={`${file.name}-${index}`} className="relative">
                <img
                  src={previews[index]}
                  alt={file.name || "Attached image"}
                  className="h-14 w-14 rounded-lg border border-rule object-cover"
                />
                <button
                  type="button"
                  aria-label={`Remove ${file.name || "image"}`}
                  onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-rule bg-paper text-2xs text-muted"
                >
                  ✕
                </button>
              </span>
            ))}
            <button
              type="button"
              className="text-xs text-accent underline underline-offset-2"
              onClick={() => setFiles([])}
            >
              clear
            </button>
          </div>
        ) : null}

        <form
          className="mt-3 flex shrink-0 items-end gap-2 border-t border-rule pt-3"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
          // Screenshot → ⌘V. The clipboard is where a receipt usually already is, and
          // making people save it to a file first is a step for the computer's benefit.
          onPaste={(event) => {
            const pasted = Array.from(event.clipboardData.files);
            if (!pasted.some((file) => file.type.startsWith("image/"))) return;
            event.preventDefault();
            void acceptImages(pasted);
          }}
        >
          {/*
            Two inputs, not one. `capture` forces the camera and hides the photo library
            entirely on a phone, which is the wrong default — most receipts have already
            been photographed by the time anyone opens this. The library button is the
            plain input; the camera button is the one that asks for the camera.
          */}
          <label
            className="tap flex cursor-pointer items-center justify-center rounded-lg border border-rule px-2.5 text-muted transition-colors hover:border-ink/25 hover:text-ink"
            title="Choose a photo"
          >
            <PhotoIcon />
            <span className="sr-only">Choose a photo</span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(event) => void acceptImages(Array.from(event.target.files ?? []))}
            />
          </label>
          <label
            className="tap flex cursor-pointer items-center justify-center rounded-lg border border-rule px-2.5 text-muted transition-colors hover:border-ink/25 hover:text-ink md:hidden"
            title="Take a photo"
          >
            <CameraIcon />
            <span className="sr-only">Take a photo</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(event) => void acceptImages(Array.from(event.target.files ?? []))}
            />
          </label>
          <Input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.shiftKey) return;
              // Phone keyboards do not always submit the form on their Go key.
              event.preventDefault();
              void send();
            }}
            enterKeyHint="send"
            placeholder="What did you pay for?"
            aria-label="Message the assistant"
            disabled={busy}
          />
          <Button type="submit" variant="primary" disabled={busy}>
            Send
          </Button>
        </form>

        <details className="mt-3 shrink-0 border-t border-rule pt-3">
          <summary className="cursor-pointer text-xs text-muted">What I did here</summary>
          {actions.isPending ? (
            <Spinner />
          ) : (
            <ul className="mt-2 divide-y divide-rule text-xs">
              {(actions.data ?? []).map((action) => (
                <li key={action.id} className="flex items-baseline justify-between gap-3 py-1.5">
                  <span>
                    <span className="font-medium">{action.tool.replace(/_/g, " ")}</span>{" "}
                    <span
                      className={cn(
                        "text-muted",
                        action.status === "failed" && "text-owing",
                        action.status === "confirmed" && "text-owed",
                      )}
                    >
                      {action.status}
                    </span>
                  </span>
                  <span className="num text-muted">
                    {action.latency_ms ? `${action.latency_ms} ms` : ""}
                    {action.input_tokens
                      ? ` · ${action.input_tokens + (action.output_tokens ?? 0)} tok`
                      : ""}
                  </span>
                </li>
              ))}
              {(actions.data ?? []).length === 0 ? (
                <li className="py-2 text-muted">Nothing yet.</li>
              ) : null}
            </ul>
          )}
        </details>
      </div>
    </div>
  );
}

function ProposalCard({
  card,
  groupId,
  currency,
  names,
  resolved,
  busy,
  onConfirm,
  onReject,
}: {
  card: Proposal;
  groupId: number;
  currency: string;
  names: Map<number, string>;
  resolved?: "confirmed" | "rejected";
  busy: boolean;
  onConfirm: (draft: ProposalDraft) => void;
  onReject: () => void;
}) {
  const [draft, setDraft] = useState<ProposalDraft>(card.draft ?? {});
  const readOnly = card.editable === false;
  const locked = readOnly || Boolean(resolved) || busy;

  const lines = draft.line_items ?? [];
  const participantIds = (draft.participants ?? []).map((participant) => participant.member_id);
  const entryCurrency = draft.currency_code || currency;

  /**
   * Reassigning a receipt line changes every share, so the numbers have to come back
   * from the server — the same `/preview` the entry form uses, so what the card shows is
   * what saving would produce. Until it answers, the last good preview stays on screen
   * rather than the card emptying out.
   */
  const rePreview = useQuery({
    queryKey: ["ai-proposal-preview", groupId, JSON.stringify(draft)],
    queryFn: () =>
      api.post<ProposalPreview>(`/groups/${groupId}/transactions/preview`, {
        type: draft.type ?? "expense",
        name: draft.name,
        occurred_on: draft.occurred_on,
        total_original_cents: draft.total_original_cents,
        currency_code: draft.currency_code ?? undefined,
        fx_rate_to_base: (draft.fx_rate_to_base as string | undefined) ?? undefined,
        payer_member_id: draft.payer_member_id,
        split: draft.split ?? "even",
        participants: draft.participants ?? [],
        group_cost_original_cents: draft.group_cost_original_cents ?? 0,
        individual_source: draft.individual_source,
        line_items: draft.line_items ?? [],
      }),
    // Only worth asking again once something has actually been changed here.
    enabled: !locked && lines.length > 0 && draft !== card.draft,
    retry: false,
    placeholderData: (previous) => previous,
  });

  const shown = rePreview.data ?? card.preview;
  const recomputing = rePreview.isFetching;

  const assign = (lineIndex: number, memberId: number) => {
    setDraft((current) => {
      const next = (current.line_items ?? []).map((line, index) => {
        if (index !== lineIndex) return line;
        const has = line.assignee_member_ids.includes(memberId);
        return {
          ...line,
          assignee_member_ids: has
            ? line.assignee_member_ids.filter((id) => id !== memberId)
            : [...line.assignee_member_ids, memberId],
        };
      });
      // Line items only mean anything on an individual split, and the server derives the
      // per-person amounts from them — so say so explicitly rather than relying on a
      // default that could change.
      return {
        ...current,
        line_items: next,
        split: "individual",
        individual_source: "line_items",
      };
    });
  };

  const splitLabel =
    draft.split === "percentage"
      ? "Split by percentage"
      : draft.split === "individual"
        ? "Split by what each person had"
        : "Split evenly";

  const totalForPercent = shown?.total_base_cents ?? 0;
  const percentOf = (cents: number) => {
    if (draft.split !== "percentage" || totalForPercent <= 0) return "";
    return `  ${((cents * 100) / totalForPercent).toFixed(1)}%`;
  };

  if (card.kind === "delete") {
    return (
      <div className="w-full animate-scale-in rounded-xl border border-rule bg-raised p-3.5 text-sm">
        <p className="mb-1 text-2xs uppercase tracking-widest text-muted">Proposed deletion</p>
        <p>
          {card.summary?.name} · {card.summary?.occurred_on} ·{" "}
          <span className="num">{formatMoney(card.summary?.total_base_cents ?? 0, currency)}</span>
        </p>
        {card.reason ? <p className="mt-1 text-xs text-muted">{card.reason}</p> : null}
        {resolved ? (
          <p className="mt-2 text-xs text-muted">
            {resolved === "confirmed" ? "Deleted. An admin can restore it." : "Dismissed."}
          </p>
        ) : readOnly ? (
          <p className="mt-2 text-xs text-muted">
            The assistant cannot delete this entry: {card.refused_because ?? "you do not have permission"}.
          </p>
        ) : (
          <div className="mt-3 flex gap-2">
            <Button variant="danger" disabled={busy} onClick={() => onConfirm({})}>
              Delete it
            </Button>
            <Button onClick={onReject}>Keep it</Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full animate-scale-in rounded-xl border border-accent/40 bg-raised p-3.5 shadow-sm">
      <p className="mb-2 text-2xs uppercase tracking-widest text-muted">
        Proposed {card.kind}
      </p>

      <div className="flex flex-col gap-2">
        <label className="text-xs text-muted">
          Name
          <Input
            className="mt-1 h-9"
            value={String(draft.name ?? "")}
            disabled={readOnly || Boolean(resolved)}
            // The assistant's guess at a name is a starting point, and the usual reason
            // to touch this field is to replace it outright. Selecting it on focus means
            // one tap and type, instead of tapping in, then selecting all, then typing —
            // which on a phone means a long-press and a fiddly handle drag.
            onFocus={(event) => event.currentTarget.select()}
            // Safari on iOS collapses the selection right after focus when the tap
            // lands inside existing text, so re-apply it once the tap has settled.
            onPointerUp={(event) => {
              const field = event.currentTarget;
              if (field.selectionStart !== field.selectionEnd) return;
              requestAnimationFrame(() => field.select());
            }}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
        </label>
        <div className="flex gap-2">
          <label className="flex-1 text-xs text-muted">
            Date
            <Input
              type="date"
              className="mt-1 h-9"
              value={String(draft.occurred_on ?? "")}
              disabled={readOnly || Boolean(resolved)}
              onChange={(event) => setDraft({ ...draft, occurred_on: event.target.value })}
            />
          </label>
          <div className="flex-1 text-xs text-muted">
            Total
            <p className="num mt-1 py-2 text-base text-ink">
              {formatMoney(shown?.total_base_cents ?? 0, currency)}
            </p>
          </div>
        </div>
      </div>

      {/*
        The assistant has no live rates, and a prompt cannot reliably stop a model
        producing a plausible-looking one from memory. So a converted entry never slips
        through quietly: the rate is shown as a number to check, because it is frozen onto
        the entry the moment this is confirmed.
      */}
      {draft.currency_code && draft.currency_code !== currency ? (
        <div className="mt-3 rounded-lg border border-owing/35 bg-owing/[0.06] px-3 py-2 text-xs">
          <p>
            Converted at <span className="num font-medium">{String(draft.fx_rate_to_base ?? "?")}</span>{" "}
            {currency} per {draft.currency_code}. Check this — the assistant cannot look up
            today's rate, and whatever is here is saved onto the entry.
          </p>
        </div>
      ) : null}

      {lines.length > 0 ? (
        <section className="mt-3 rounded-lg border border-rule bg-sunken p-2.5">
          <h3 className="mb-1.5 text-2xs uppercase tracking-widest text-muted">
            Receipt lines — tap a name to say who had it
          </h3>
          <ul className="flex flex-col gap-2">
            {lines.map((line, index) => (
              <li key={`${line.name}-${index}`}>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">{line.name}</span>
                  <span className="num shrink-0">
                    {formatMoney(line.amount_original_cents, entryCurrency)}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {participantIds.map((memberId) => {
                    const on = line.assignee_member_ids.includes(memberId);
                    return (
                      <button
                        key={memberId}
                        type="button"
                        disabled={locked}
                        aria-pressed={on}
                        onClick={() => assign(index, memberId)}
                        className={cn(
                          "chip min-h-[30px] px-2.5 text-xs",
                          on && "chip-on",
                          locked && "opacity-60",
                        )}
                      >
                        {names.get(memberId) ?? `#${memberId}`}
                      </button>
                    );
                  })}
                </div>
                {line.assignee_member_ids.length === 0 ? (
                  <p className="mt-0.5 text-2xs text-muted">Shared by everyone</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* The split itself, in words, so it can be checked without opening the entry. */}
      {shown?.shares?.length ? (
        <section className="mt-3">
          <h3 className="mb-1 text-2xs uppercase tracking-widest text-muted">
            {splitLabel}
            {recomputing ? " · working it out…" : ""}
          </h3>
          <ul className="flex flex-col gap-0.5 text-xs">
            {shown.shares.map((share) => (
              <li key={share.member_id} className="flex items-baseline justify-between gap-3">
                <span className="truncate">{names.get(share.member_id) ?? `#${share.member_id}`}</span>
                <span className="num shrink-0">
                  {formatMoney(share.share_base_cents, currency)}
                  {percentOf(share.share_base_cents)}
                </span>
              </li>
            ))}
          </ul>
          {shown.derived_tax_tip_original_cents ? (
            <p className="mt-1 text-2xs text-muted">
              {shown.derived_tax_tip_original_cents < 0
                ? `Less ${formatMoney(-shown.derived_tax_tip_original_cents, entryCurrency)} off the total, taken`
                : `Includes ${formatMoney(shown.derived_tax_tip_original_cents, entryCurrency)} of tax and tip, spread`}{" "}
              in proportion to each share.
            </p>
          ) : null}
        </section>
      ) : null}

      {/* And the balance effect: what each person's number actually moves by. */}
      {shown ? (
        <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {Object.entries(shown.ledger)
            .map(([memberId, delta]) => [Number(memberId), delta] as const)
            .sort((a, b) => b[1] - a[1])
            .map(([memberId, delta]) => (
              <li key={memberId}>
                <span className="text-muted">{names.get(memberId) ?? `#${memberId}`} </span>
                <span className={cn("num", toneClass(delta))}>
                  {formatSigned(delta, currency)}
                </span>
              </li>
            ))}
        </ul>
      ) : null}

      {shown?.warnings?.map((warning) => (
        <p key={warning} className="mt-1 text-xs text-owing">
          {warning}
        </p>
      ))}

      {/*
        The same "did I already add this?" the entry form asks. A proposal is confirmed
        with one tap, and a receipt photographed twice looks exactly like a receipt
        photographed once — so this only ever asks, and never blocks.
      */}
      {!resolved && shown?.possible_duplicates?.length ? (
        <div className="mt-3 rounded-lg border border-owing/35 bg-owing/[0.06] px-3 py-2 text-xs">
          <p className="font-medium">
            Already in the ledger?{" "}
            <span className="font-normal text-muted">
              Same amount, around the same date.
            </span>
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {shown.possible_duplicates.map((candidate) => (
              <li key={candidate.transaction_id} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">{candidate.name}</span>
                <span className="num shrink-0 text-muted">
                  {candidate.occurred_on} ·{" "}
                  {formatMoney(candidate.total_base_cents, currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {resolved ? (
        <p className="mt-3 text-xs text-muted">
          {resolved === "confirmed" ? "Added to the ledger." : "Dismissed."}
        </p>
      ) : readOnly ? (
        <p className="mt-3 text-xs text-muted">
          The assistant cannot change this entry: {card.refused_because ?? "you do not have permission"}.
        </p>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button variant="primary" disabled={busy} onClick={() => onConfirm(draft)}>
            {card.kind === "edit" ? "Save changes" : "Add entry"}
          </Button>
          <Button onClick={onReject}>Dismiss</Button>
        </div>
      )}
    </div>
  );
}
